import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Activity,
  Phone,
  ShieldCheck,
  Zap,
  MessageCircle,
  PlayCircle,
} from "lucide-react";
import { Button } from "../ui/Button";
import { IndustryBadges } from "../ui/IndustryBadges";
import { StatsBar } from "../ui/StatsBar";
import { IndustryRotator } from "../ui/IndustryRotator";

// --- Live Activity Feed Component ---

interface ActivityLog {
  id: string;
  action: string;
  detail: string;
  icon: React.ElementType;
  color: string;
}

const activities = [
  {
    action: "Agent answered call",
    detail: "Apex Roofing (Austin, TX)",
    icon: Phone,
    color: "text-blue-400",
  },
  {
    action: "Lead qualified",
    detail: "Emergency Leak - $2.5k Est.",
    icon: Zap,
    color: "text-yellow-400",
  },
  {
    action: "Appointment booked",
    detail: "Tomorrow at 9:00 AM",
    icon: Activity,
    color: "text-green-400",
  },
  {
    action: "Spam call blocked",
    detail: "Robocall prevented",
    icon: ShieldCheck,
    color: "text-red-400",
  },
  {
    action: "Follow-up sent",
    detail: "Lead reactivation SMS",
    icon: MessageCircle,
    color: "text-purple-400",
  },
];

const LiveActivityFeed = () => {
  const [logs, setLogs] = useState<ActivityLog[]>(() =>
    activities.slice(0, 3).map((a) => ({ ...a, id: crypto.randomUUID() }))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const randomActivity =
        activities[Math.floor(Math.random() * activities.length)];
      const newLog = { ...randomActivity, id: crypto.randomUUID() };

      setLogs((prev) => [newLog, ...prev.slice(0, 2)]);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute top-32 right-4 md:right-12 z-20 hidden lg:block">
      <div className="bg-brand-dark/80 backdrop-blur-md border border-brand-border rounded-xl p-4 w-80 shadow-2xl">
        <div className="flex items-center gap-2 mb-3 border-b border-brand-border/50 pb-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">
            Live System Activity
          </span>
        </div>
        <div className="space-y-3 relative h-32 overflow-hidden">
          {/* Mask fade at bottom */}
          <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-brand-dark/80 to-transparent z-10" />

          <AnimatePresence initial={false}>
            {logs.map((log) => (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: 20, height: 0 }}
                transition={{ duration: 0.4 }}
                className="flex items-start gap-3"
              >
                <div className={`mt-1 p-1 rounded bg-brand-base ${log.color}`}>
                  <log.icon className="w-3 h-3" />
                </div>
                <div>
                  <div className="text-xs text-slate-200 font-medium">
                    {log.action}
                  </div>
                  <div className="text-[10px] text-slate-500">{log.detail}</div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      {/* Active Agents Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="absolute -bottom-16 right-0 bg-brand-primary/10 border border-brand-primary/20 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2"
      >
        <Zap className="w-4 h-4 text-brand-primary fill-brand-primary" />
        <span className="text-xs text-brand-primary font-bold">
          1,248 Agents Online
        </span>
      </motion.div>
    </div>
  );
};

// --- Hero Component ---

interface HeroProps {
  onBookCall: () => void;
}

export const Hero = ({ onBookCall }: HeroProps) => {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Live Activity Feed (Desktop) */}
        <LiveActivityFeed />

        <div className="text-center max-w-4xl mx-auto lg:text-left lg:mx-0 lg:max-w-3xl">
          {/* Industry Badges - Show diversity */}
          <IndustryBadges />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-brand-base border border-brand-border mb-6"
          >
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
            <span className="text-sm text-slate-300 font-medium">
              Limited Spots for Q1 2025
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 font-display"
          >
            Stop Losing Customers <br className="hidden sm:block" />
            <span className="text-brand-accent">While You're Busy Working</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-slate-300 mb-8 max-w-readable mx-auto lg:mx-0 font-medium text-xl"
          >
            AI-powered phone answering that captures every opportunity
            24/7—trusted by{" "}
            <span className="text-white font-semibold">
              contractors, consultants, and service businesses
            </span>{" "}
            across industries.
          </motion.p>

          {/* Rotating Industry Examples */}
          <div className="h-8 mb-8 overflow-hidden relative">
            <IndustryRotator />
          </div>

          {/* Stats Bar - Social proof */}
          <StatsBar />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                variant="accent"
                size="lg"
                className="group shadow-[0_0_30px_rgba(6,182,212,0.3)]"
                onClick={onBookCall}
              >
                FREE 5-Minute Business Assessment
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() =>
                  document
                    .getElementById("results")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                See How Much You're Losing
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-500 font-medium uppercase tracking-wide">
              No credit card • See real numbers • 100% free
            </p>
          </motion.div>

          {/* Audio Preview - CRO Enhancement */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-8 text-center lg:text-left"
          >
            <p className="text-brand-accent text-xs font-bold uppercase tracking-wider mb-3">
              Hear It In Action
            </p>
            <button
              onClick={() =>
                document
                  .getElementById("audio-samples")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex items-center gap-3 text-slate-300 hover:text-brand-primary transition-colors group"
            >
              <div className="p-2 rounded-full bg-brand-base border border-brand-border group-hover:border-brand-primary/50 transition-colors">
                <PlayCircle className="w-6 h-6 text-brand-primary" />
              </div>
              <span className="text-sm font-medium border-b border-transparent group-hover:border-brand-primary/50">
                Listen to a 30-second sample call
              </span>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
